<template>
  <div class="project-appeal">
    <div class="page-header">
      <h3>申诉处理</h3>
    </div>

    <!-- 批次筛选 -->
    <div class="filter-bar">
      <el-select v-model="selectedBatchId" placeholder="选择申诉批次" clearable @change="loadRecords">
        <el-option 
          v-for="batch in batches" 
          :key="batch.id" 
          :label="`${batch.batchName || batch.batchNo} (${batch.answerCount}份)`" 
          :value="batch.id"
        ></el-option>
      </el-select>
      <el-input 
        v-model="searchKeyword" 
        placeholder="搜索店代码/店名称" 
        prefix-icon="el-icon-search"
        clearable
        style="width: 200px"
        @keyup.enter.native="loadRecords"
      ></el-input>
      <el-button type="primary" icon="el-icon-search" @click="loadRecords">搜索</el-button>
    </div>

    <!-- 批次时间信息 -->
    <div class="batch-info" v-if="selectedBatch">
      <div class="info-item">
        <span class="label">申诉开始时间：</span>
        <span class="value">{{ formatTime(selectedBatch.startTime) }}</span>
      </div>
      <div class="info-item">
        <span class="label">申诉结束时间：</span>
        <span class="value">{{ formatTime(selectedBatch.endTime) }}</span>
      </div>
      <div class="info-item">
        <el-tag :type="getAppealTimeStatus.type" size="small">{{ getAppealTimeStatus.text }}</el-tag>
      </div>
      <div class="info-item">
        <el-button type="text" icon="el-icon-edit" @click="showEditTimeDialog">修改时间</el-button>
      </div>
    </div>

    <!-- 修改时间对话框 -->
    <el-dialog title="修改申诉时间" :visible.sync="editTimeDialogVisible" width="500px">
      <el-form :model="editTimeForm" label-width="120px">
        <el-form-item label="申诉开始时间">
          <el-date-picker
            v-model="editTimeForm.startTime"
            type="datetime"
            placeholder="选择开始时间"
            style="width: 100%"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="申诉结束时间">
          <el-date-picker
            v-model="editTimeForm.endTime"
            type="datetime"
            placeholder="选择结束时间"
            style="width: 100%"
          ></el-date-picker>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="editTimeDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="editTimeSubmitting" @click="submitEditTime">确定</el-button>
      </div>
    </el-dialog>


    <!-- 申诉记录列表（按店） -->
    <el-table :data="records" v-loading="loading" border stripe style="width: 100%">
      <el-table-column type="index" label="序号" width="60"></el-table-column>
      <el-table-column prop="dealerCode" label="经销商代码" min-width="120">
        <template slot-scope="{ row }">{{ row.dealerCode || '-' }}</template>
      </el-table-column>
      <el-table-column prop="dealerName" label="经销商名称" min-width="150">
        <template slot-scope="{ row }">{{ row.dealerName || '-' }}</template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template slot-scope="{ row }">
          <el-tag :type="getStatusType(row.status)" size="small">
            {{ getStatusText(row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="提交时间" min-width="150">
        <template slot-scope="{ row }">
          {{ formatTime(row.submitTime) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="150" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" @click="viewDetail(row)">查看详情</el-button>
          <el-button type="text" @click="goToReviewPage()" v-if="row.status === 1">审核</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="loadRecords"
      ></el-pagination>
    </div>

    <!-- 申诉详情对话框（只读查看） -->
    <el-dialog title="申诉详情" :visible.sync="detailDialogVisible" width="900px">
      <div v-if="currentRecord" class="appeal-detail readonly-mode">
        <div class="detail-header">
          <div class="info-item">
            <label>经销商：</label>
            <span>{{ currentRecord.dealerName }} ({{ currentRecord.dealerCode }})</span>
          </div>
          <div class="info-item">
            <label>状态：</label>
            <el-tag :type="getStatusType(currentRecord.status)" size="small">
              {{ getStatusText(currentRecord.status) }}
            </el-tag>
          </div>
        </div>
        
        <el-divider content-position="left">申诉题目</el-divider>
        
        <!-- 题目列表（只读） -->
        <div class="appeal-items">
          <div v-for="item in currentItems" :key="item.id" class="appeal-item">
            <div class="item-header">
              <span class="item-label">{{ item.itemLabel }}</span>
              <el-tag :type="getReviewStatusType(item.reviewStatus)" size="mini">
                {{ getReviewStatusText(item.reviewStatus) }}
              </el-tag>
            </div>
            
            <div class="item-content">
              <div class="remark-section">
                <label>申诉备注：</label>
                <p>{{ item.appealRemark || '无' }}</p>
              </div>
              
              <div class="evidence-section" v-if="item.evidenceUrls">
                <label>证据材料：</label>
                <div class="evidence-list" v-viewer="{movable: false}">
                  <template v-for="(file, idx) in parseEvidenceUrls(item.evidenceUrls)">
                    <div v-if="isImage(file)" :key="idx" class="evidence-item image">
                      <img :src="file.url" />
                      <span class="file-name">{{ file.name || '图片' }}</span>
                    </div>
                    <div v-else :key="idx" class="evidence-item file">
                      <a :href="file.url" target="_blank">
                        <i class="el-icon-document"></i>
                        {{ file.name || '附件' }}
                      </a>
                    </div>
                  </template>
                </div>
              </div>
              
              <!-- 已审核状态显示 -->
              <div class="review-result" v-if="item.reviewStatus !== 0">
                <div class="result-remark" v-if="item.reviewRemark">
                  <label>审核备注：</label>
                  <span>{{ item.reviewRemark }}</span>
                </div>
                <div class="result-score" v-if="item.reviewStatus === 1">
                  <label>还分：</label>
                  <span class="score">+{{ item.restoredScore }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>
    
    <!-- 申诉审核对话框（带审核功能） -->
    <el-dialog 
      title="申诉审核" 
      :visible.sync="reviewDialogVisible" 
      width="1000px"
      :close-on-click-modal="false"
    >
      <div v-if="currentRecord" class="appeal-review">
        <div class="review-header">
          <div class="info-group">
            <span class="dealer-info">{{ currentRecord.dealerName }} ({{ currentRecord.dealerCode }})</span>
            <el-tag :type="getStatusType(currentRecord.status)" size="small">
              {{ getStatusText(currentRecord.status) }}
            </el-tag>
          </div>
        </div>
        
        <!-- 题目列表（可审核） -->
        <div class="appeal-items-review">
          <div v-for="item in currentItems" :key="item.id" class="appeal-item-card">
            <div class="card-header">
              <span class="item-label">{{ item.itemLabel }}</span>
              <el-tag :type="getReviewStatusType(item.reviewStatus)" size="mini">
                {{ getReviewStatusText(item.reviewStatus) }}
              </el-tag>
            </div>
            
            <div class="card-body">
              <div class="appeal-content">
                <div class="field">
                  <label>申诉说明：</label>
                  <div class="value">{{ item.appealRemark || '无' }}</div>
                </div>
                
                <div class="field" v-if="item.evidenceUrls">
                  <label>凭证材料：</label>
                  <div class="evidence-gallery" v-viewer="{movable: false}">
                    <template v-for="(file, idx) in parseEvidenceUrls(item.evidenceUrls)">
                      <div v-if="isImage(file)" :key="idx" class="evidence-thumb">
                        <img :src="file.url" />
                      </div>
                      <a v-else :key="idx" :href="file.url" target="_blank" class="evidence-file">
                        <i class="el-icon-document"></i>
                        {{ file.name || '附件' }}
                      </a>
                    </template>
                  </div>
                </div>
              </div>
              
              <!-- 审核操作区 -->
              <div class="review-action-area" v-if="item.reviewStatus === 0">
                <div class="action-row">
                  <el-input 
                    v-model="item.reviewRemarkInput" 
                    placeholder="审核备注（可选）"
                    size="small"
                    style="flex: 1; max-width: 400px;"
                  ></el-input>
                  <el-input-number 
                    v-model="item.restoredScoreInput" 
                    :min="0" 
                    :max="item.originalScore"
                    placeholder="还分" 
                    size="small"
                    style="width: 120px;"
                    controls-position="right"
                  ></el-input-number>
                </div>
                <div class="action-buttons">
                  <el-button type="success" size="small" icon="el-icon-check" @click="reviewItem(item, 1)">通过</el-button>
                  <el-button type="danger" size="small" icon="el-icon-close" @click="reviewItem(item, 2)">不通过</el-button>
                </div>
              </div>
              
              <!-- 已审核结果 -->
              <div class="reviewed-result" v-else>
                <el-alert 
                  :type="item.reviewStatus === 1 ? 'success' : 'error'" 
                  :closable="false"
                  show-icon
                >
                  <template slot="title">
                    {{ item.reviewStatus === 1 ? '已通过' : '已驳回' }}
                    <span v-if="item.restoredScore" class="restored-score">（还分 +{{ item.restoredScore }}）</span>
                  </template>
                  <span v-if="item.reviewRemark">{{ item.reviewRemark }}</span>
                </el-alert>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="reviewDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="finishReview" :disabled="!hasUnreviewed">完成审核</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'index/axios'

export default {
  name: 'ProjectAppeal',

  data() {
    return {
      loading: false,
      batches: [],
      selectedBatchId: null,
      searchKeyword: '',
      records: [],
      page: 1,
      pageSize: 20,
      total: 0,
      // 详情（只读）
      detailDialogVisible: false,
      // 审核
      reviewDialogVisible: false,
      currentRecord: null,
      currentItems: [],
      // 编辑时间
      editTimeDialogVisible: false,
      editTimeSubmitting: false,
      editTimeForm: {
        startTime: '',
        endTime: ''
      }
    }
  },

  computed: {
    projectId() {
      return this.$route.params.projectId
    },
    // 当前选中的批次
    selectedBatch() {
      return this.batches.find(b => b.id === this.selectedBatchId) || null
    },
    // 申诉时间状态
    getAppealTimeStatus() {
      if (!this.selectedBatch) return { type: 'info', text: '-' }
      
      const now = new Date()
      const start = this.selectedBatch.startTime ? new Date(this.selectedBatch.startTime) : null
      const end = this.selectedBatch.endTime ? new Date(this.selectedBatch.endTime) : null
      
      if (!start || !end) return { type: 'info', text: '时间未设置' }
      
      if (now < start) {
        return { type: 'warning', text: '未开始' }
      } else if (now > end) {
        return { type: 'danger', text: '已结束' }
      } else {
        return { type: 'success', text: '进行中' }
      }
    },
    // 是否有未审核的题目
    hasUnreviewed() {
      return this.currentItems.some(item => item.reviewStatus === 0)
    }
  },

  mounted() {
    this.loadBatches()
  },

  methods: {
    async loadBatches() {
      try {
        console.log('[Appeal] loadBatches - projectId:', this.projectId)
        const res = await axios.post('/Appeal/batch/list', {
          projectId: Number(this.projectId),
          page: 1,
          pageSize: 100
        })
        console.log('[Appeal] loadBatches full response:', JSON.stringify(res, null, 2))
        
        // 尝试多种响应格式
        let records = res?.data?.data?.records   // { data: { data: { records } } }
                   || res?.data?.records         // { data: { records } }
                   || res?.records               // { records }
                   || []
        
        this.batches = records
        console.log('[Appeal] batches:', this.batches)
        
        if (this.batches.length > 0) {
          this.selectedBatchId = this.batches[0].id
          this.loadRecords()
        }
      } catch (e) {
        console.error('加载批次失败', e)
        // 尝试从错误中提取数据
        const data = e?.data?.data || e?.data
        if (data?.records) {
          this.batches = data.records
          console.log('[Appeal] batches from error:', this.batches)
          if (this.batches.length > 0) {
            this.selectedBatchId = this.batches[0].id
            this.loadRecords()
          }
        }
      }
    },

    async loadRecords() {
      if (!this.selectedBatchId) {
        this.records = []
        return
      }
      this.loading = true
      try {
        console.log('[Appeal] loadRecords - batchId:', this.selectedBatchId)
        const res = await axios.post('/Appeal/record/list', {
          batchId: this.selectedBatchId,
          dealerCode: this.searchKeyword,
          page: this.page,
          pageSize: this.pageSize
        })
        console.log('[Appeal] loadRecords full response:', JSON.stringify(res?.data, null, 2))
        
        // 解析响应: res.data.data.records
        let records = res?.data?.data?.records
                   || res?.data?.records
                   || []
        let total = res?.data?.data?.total
                 || res?.data?.total
                 || 0
        
        this.records = records
        this.total = total
        console.log('[Appeal] records:', this.records, 'total:', this.total)
      } catch (e) {
        console.error('加载申诉记录失败', e)
        // 尝试从错误中提取数据
        const data = e?.data?.data || e?.data
        if (data?.records) {
          this.records = data.records
          this.total = data.total || 0
          console.log('[Appeal] records from error:', this.records)
        }
      } finally {
        this.loading = false
      }
    },

    async viewDetail(record) {
      try {
        console.log('[Appeal] viewDetail - recordId:', record.id)
        const res = await axios.get(`/Appeal/record/detail/${record.id}`)
        console.log('[Appeal] viewDetail full response:', JSON.stringify(res, null, 2))
        
        // 处理多层嵌套: res.data.data 或 res.data
        const data = res?.data?.data || res?.data || res
        
        this.currentRecord = data.record || record
        this.currentItems = (data.items || []).map(item => ({
          ...item,
          reviewRemarkInput: '',
          restoredScoreInput: item.originalScore || 0
        }))
        
        console.log('[Appeal] currentRecord:', this.currentRecord)
        console.log('[Appeal] currentItems:', this.currentItems)
        
        this.detailDialogVisible = true
      } catch (e) {
        console.error('加载详情失败', e)
        // 尝试从错误中提取数据
        const data = e?.data?.data || e?.data
        if (data?.items) {
          this.currentRecord = data.record || record
          this.currentItems = (data.items || []).map(item => ({
            ...item,
            reviewRemarkInput: '',
            restoredScoreInput: item.originalScore || 0
          }))
          this.detailDialogVisible = true
        } else {
          this.$message.error('加载详情失败')
        }
      }
    },

    async processAppeal(record) {
      // 加载详情并打开审核弹窗
      try {
        console.log('[Appeal] processAppeal - recordId:', record.id)
        const res = await axios.get(`/Appeal/record/detail/${record.id}`)
        console.log('[Appeal] processAppeal response:', JSON.stringify(res, null, 2))
        
        const data = res?.data?.data || res?.data || res
        
        this.currentRecord = data.record || record
        this.currentItems = (data.items || []).map(item => ({
          ...item,
          reviewRemarkInput: '',
          restoredScoreInput: item.originalScore || 0
        }))
        
        this.reviewDialogVisible = true
      } catch (e) {
        console.error('加载详情失败', e)
        const data = e?.data?.data || e?.data
        if (data?.items) {
          this.currentRecord = data.record || record
          this.currentItems = (data.items || []).map(item => ({
            ...item,
            reviewRemarkInput: '',
            restoredScoreInput: item.originalScore || 0
          }))
          this.reviewDialogVisible = true
        } else {
          this.$message.error('加载详情失败')
        }
      }
    },
    
    async finishReview() {
      // 检查是否所有题目都已审核
      const unreviewedCount = this.currentItems.filter(item => item.reviewStatus === 0).length
      if (unreviewedCount > 0) {
        this.$message.warning(`还有 ${unreviewedCount} 个题目未审核`)
        return
      }
      
      this.$message.success('审核完成')
      this.reviewDialogVisible = false
      // 刷新列表
      this.loadRecords()
    },

    async reviewItem(item, status) {
      try {
        await axios.post('/Appeal/item/review', {
          itemId: item.id,
          reviewStatus: status,
          reviewRemark: item.reviewRemarkInput,
          restoredScore: status === 1 ? item.restoredScoreInput : 0
        })
        this.$message.success(status === 1 ? '审核通过，已还分' : '审核不通过')
        
        // 更新本地状态
        item.reviewStatus = status
        item.reviewRemark = item.reviewRemarkInput
        if (status === 1) {
          item.restoredScore = item.restoredScoreInput
        }
      } catch (e) {
        console.error('审核失败', e)
        this.$message.error('审核失败')
      }
    },

    getStatusType(status) {
      const map = { 0: 'info', 1: 'warning', 2: 'primary', 3: 'success' }
      return map[status] || 'info'
    },

    getStatusText(status) {
      const map = { 0: '待申诉', 1: '已提交', 2: '审核中', 3: '已完成' }
      return map[status] || '未知'
    },

    getReviewStatusType(status) {
      const map = { 0: 'info', 1: 'success', 2: 'danger' }
      return map[status] || 'info'
    },

    getReviewStatusText(status) {
      const map = { 0: '待审核', 1: '已通过', 2: '不通过' }
      return map[status] || '未知'
    },

    formatTime(timeStr) {
      if (!timeStr) return '-'
      return new Date(timeStr).toLocaleString('zh-CN')
    },

    parseEvidenceUrls(urlsStr) {
      if (!urlsStr) return []
      try {
        const parsed = JSON.parse(urlsStr)
        // 新格式: [{name, url}, ...]
        if (Array.isArray(parsed)) {
          return parsed.map(item => {
            // 如果是对象，提取 url
            if (typeof item === 'object' && item.url) {
              return item
            }
            // 如果是字符串，转换为对象
            return { url: item, name: '' }
          })
        }
        return []
      } catch {
        // 旧格式: 逗号分隔的 URL
        return urlsStr.split(',').filter(u => u).map(url => ({ url, name: '' }))
      }
    },

    isImage(urlOrFile) {
      const url = typeof urlOrFile === 'object' ? urlOrFile.url : urlOrFile
      if (!url) return false
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url) || url.includes('/image/')
    },

    previewImage(url) {
      window.open(url, '_blank')
    },

    // 跳转到独立审核页面
    goToReviewPage() {
      if (!this.selectedBatchId) {
        this.$message.warning('请先选择申诉批次')
        return
      }
      this.$router.push({
        path: '/appeal-review',
        query: { batchId: this.selectedBatchId }
      })
    },

    // 显示编辑时间对话框
    showEditTimeDialog() {
      if (!this.selectedBatch) return
      this.editTimeForm.startTime = this.selectedBatch.startTime ? new Date(this.selectedBatch.startTime) : ''
      this.editTimeForm.endTime = this.selectedBatch.endTime ? new Date(this.selectedBatch.endTime) : ''
      this.editTimeDialogVisible = true
    },

    // 提交编辑时间
    async submitEditTime() {
      if (!this.editTimeForm.startTime || !this.editTimeForm.endTime) {
        this.$message.warning('请选择开始和结束时间')
        return
      }
      
      if (new Date(this.editTimeForm.startTime) >= new Date(this.editTimeForm.endTime)) {
        this.$message.warning('结束时间必须大于开始时间')
        return
      }
      
      this.editTimeSubmitting = true
      try {
        await axios.post('/Appeal/batch/updateTime', {
          batchId: this.selectedBatchId,
          startTime: this.editTimeForm.startTime,
          endTime: this.editTimeForm.endTime
        })
        
        this.$message.success('修改成功')
        this.editTimeDialogVisible = false
        
        // 刷新批次列表
        await this.loadBatches()
      } catch (e) {
        console.error('修改时间失败', e)
        this.$message.error('修改失败')
      } finally {
        this.editTimeSubmitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.project-appeal {
  padding: 0;
}

.page-header {
  margin-bottom: 20px;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 500;
  }
}

.filter-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.batch-info {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 6px;
  
  .info-item {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .label {
      color: #909399;
      font-size: 13px;
    }
    
    .value {
      color: #303133;
      font-weight: 500;
    }
  }
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.appeal-detail {
  .detail-header {
    display: flex;
    gap: 32px;
    margin-bottom: 16px;
    
    .info-item {
      display: flex;
      align-items: center;
      gap: 8px;
      
      label {
        color: #909399;
      }
    }
  }
}

.appeal-items {
  .appeal-item {
    background: #fafafa;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
    
    .item-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      
      .item-label {
        font-weight: 500;
      }
    }
    
    .item-content {
      .remark-section, .evidence-section {
        margin-bottom: 12px;
        
        label {
          color: #909399;
          font-size: 13px;
          display: block;
          margin-bottom: 4px;
        }
        
        p {
          margin: 0;
          color: #606266;
        }
      }
      
      .evidence-list {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        
        .evidence-item {
          img {
            width: 80px;
            height: 80px;
            object-fit: cover;
            border-radius: 4px;
            cursor: pointer;
            border: 1px solid #eee;
          }
        }
      }
      
      .review-action {
        display: flex;
        gap: 12px;
        align-items: center;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px dashed #eee;
      }
      
      .review-result {
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px dashed #eee;
        display: flex;
        gap: 24px;
        
        label {
          color: #909399;
        }
        
        .score {
          color: #67c23a;
          font-weight: 600;
        }
      }
    }
  }
}

// 审核弹窗样式
.appeal-review {
  .review-header {
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #eee;
    
    .info-group {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .dealer-info {
        font-size: 16px;
        font-weight: 500;
        color: #303133;
      }
    }
  }
  
  .appeal-items-review {
    max-height: 500px;
    overflow-y: auto;
    
    .appeal-item-card {
      background: #f9fafc;
      border-radius: 8px;
      margin-bottom: 16px;
      overflow: hidden;
      
      .card-header {
        padding: 12px 16px;
        background: #f0f2f5;
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        .item-label {
          font-weight: 500;
          color: #303133;
        }
      }
      
      .card-body {
        padding: 16px;
        
        .appeal-content {
          .field {
            margin-bottom: 12px;
            
            label {
              color: #909399;
              font-size: 13px;
              display: block;
              margin-bottom: 4px;
            }
            
            .value {
              color: #606266;
            }
          }
          
          .evidence-gallery {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            
            .evidence-thumb {
              img {
                width: 60px;
                height: 60px;
                object-fit: cover;
                border-radius: 4px;
                cursor: pointer;
                border: 1px solid #e4e7ed;
                
                &:hover {
                  border-color: #409eff;
                }
              }
            }
            
            .evidence-file {
              display: inline-flex;
              align-items: center;
              gap: 4px;
              padding: 4px 8px;
              background: #f0f2f5;
              border-radius: 4px;
              color: #409eff;
              font-size: 13px;
              text-decoration: none;
              
              &:hover {
                background: #e6f0ff;
              }
            }
          }
        }
        
        .review-action-area {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px dashed #e4e7ed;
          
          .action-row {
            display: flex;
            gap: 12px;
            align-items: center;
            margin-bottom: 12px;
          }
          
          .action-buttons {
            display: flex;
            gap: 8px;
          }
        }
        
        .reviewed-result {
          margin-top: 16px;
          
          .restored-score {
            color: #67c23a;
            font-weight: 500;
          }
        }
      }
    }
  }
}
</style>
